import {
  call, put, select, takeLatest, takeEvery, all,
} from 'redux-saga/effects';
import callAdapter from '@/utils/sagaHelper';
import {
  findWithReadStatus,
  findWithCode,
  isValidFilter,
  wildcardFilter,
} from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import MembershipService from '@/users/membership/service';
import { applySharingToModel } from '@/users/membership/helper';
import UsersService from '@/users/service';
import selectors from '@/selectors';
import { datasetLists } from '../../constants';
import {
  showDatasetMembers,
  showDatasetMembersError,
  shareDatasetError,
  shareDatasetSuccess,
  showDatasetPermissions,
  showDatasetPermissionsError,
} from '../actions';
import datasetSharingActions, { datasetManagePermissions } from '../constants';
import { destroyDataset, fetchDatasets, updateCurrentDatasetPermissions } from '../../actions';

export function* fetchDatasetMembers(action) {
  const sharing = yield select(selectors.sharingDataset);
  const members = action.assignable ? sharing.unassignedMembers : sharing.members;

  const filter = action.filter || members.filter;
  if (!isValidFilter(filter, members)) { return; }

  const { response, error } = yield call(
    callAdapter,
    MembershipService.entityMembers('dataset', sharing.code, wildcardFilter(filter), action.assignable),
  );

  if (error) {
    yield put(showDatasetMembersError(error, action.assignable));
  } else {
    yield put(showDatasetMembers(
      response.items,
      response.paging.total,
      action.assignable,
    ));
  }
}

export function* share() {
  const items = yield select(selectors.sharingDataset);
  const { code } = items;
  const { members, name } = items;

  const {
    errors,
    currentUserRemoved,
    currentUserUpdated,
    hasSuccess,
    successMinusDuplicates,
  } = yield applySharingToModel('dataset', code, members, datasetManagePermissions.SECURITY);

  if (errors.length > 0) {
    yield put(shareDatasetError(code, name, errors));
  }

  if (currentUserRemoved) {
    const categories = findWithCode(items, datasetLists, code);
    const toRefresh = findWithReadStatus(items, categories, states.SUCCESS);
    yield all(toRefresh.map((category) => fetchDatasets(category)));
    yield put(destroyDataset([{ code }], true));
  } else if (currentUserUpdated) {
    yield put(updateCurrentDatasetPermissions(code, currentUserUpdated.permissions));
  }

  if (hasSuccess) {
    yield put(shareDatasetSuccess(code, name, successMinusDuplicates, currentUserRemoved));
  }
}

export function* fetchDatasetPermissions(action) {
  const { response, error } = yield call(callAdapter, UsersService.entityPermissions('dataset', action.code));

  if (error) {
    yield put(showDatasetPermissionsError(action.category, action.code, error));
  } else {
    yield put(showDatasetPermissions(action.category, action.code, response));
  }
}

export default function* datasetSharingApi() {
  yield takeEvery(datasetSharingActions.FETCH_DATASET_MEMBERS, fetchDatasetMembers);
  yield takeLatest(datasetSharingActions.SHARE_DATASET, share);
  yield takeEvery(datasetSharingActions.FETCH_DATASET_PERMISSIONS, fetchDatasetPermissions);
}
